import { useState, useMemo, useCallback } from "react";
import { useIntl } from "react-intl";
import { CheckIcon } from "lucide-react";

import Spinner from "../../../assets/icons/Spinner";
import Message from "../../../i18n/Message";
import label from "../../../i18n/label";

import "./QuestionMessage.scss";

/**
 * Component for rendering AskUserQuestion messages from the agent.
 * Displays a wizard-style UI for answering structured questions.
 */
const QuestionMessage = ({ message, onSubmitAnswers }) => {
  const intl = useIntl();

  // Parse metadata from message
  const metadata = useMemo(() => {
    try {
      return JSON.parse(message.metadataJson || "{}");
    } catch {
      return {};
    }
  }, [message.metadataJson]);

  const { questions, answers, state, toolUseId } = metadata;
  const isAnswered = state === "answered";
  const isPending = state === "pending";

  // Wizard state
  const [wizardIndex, setWizardIndex] = useState(0);
  const [collectedAnswers, setCollectedAnswers] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [otherText, setOtherText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const currentQuestion = questions?.[wizardIndex];
  const isLastQuestion = wizardIndex === (questions?.length || 1) - 1;
  const isOtherSelected = selectedOptions.includes("Other");

  // Handle option selection
  const handleSelectOption = useCallback(
    (optionLabel) => {
      if (!currentQuestion) return;

      if (currentQuestion.multiSelect) {
        // Toggle selection for multiSelect
        setSelectedOptions((prev) =>
          prev.includes(optionLabel)
            ? prev.filter((l) => l !== optionLabel)
            : [...prev, optionLabel]
        );
      } else {
        // Single select
        setSelectedOptions([optionLabel]);
      }
    },
    [currentQuestion]
  );

  // Handle next/submit button
  const handleNext = useCallback(() => {
    if (!currentQuestion) return;

    const answer = {
      header: currentQuestion.header,
      question: currentQuestion.question,
      selectedLabel: currentQuestion.multiSelect
        ? selectedOptions.join(", ")
        : selectedOptions[0],
      customText: isOtherSelected ? otherText : null,
    };

    const newAnswers = [...collectedAnswers, answer];

    if (isLastQuestion) {
      // Submit all answers
      setIsSubmitting(true);
      setCollectedAnswers(newAnswers);
      onSubmitAnswers?.(toolUseId, newAnswers);
      // Mark as submitted to show read-only view immediately
      setIsSubmitted(true);
      setIsSubmitting(false);
    } else {
      // Move to next question
      setCollectedAnswers(newAnswers);
      setWizardIndex(wizardIndex + 1);
      setSelectedOptions([]);
      setOtherText("");
    }
  }, [
    currentQuestion,
    selectedOptions,
    isOtherSelected,
    otherText,
    collectedAnswers,
    isLastQuestion,
    toolUseId,
    wizardIndex,
    onSubmitAnswers,
  ]);

  // Render answered state (shows question with highlighted answer)
  // Handles three scenarios:
  // 1. isAnswered: metadata state is "answered" (from backend)
  // 2. isSubmitted: just submitted locally (before backend confirms)
  // 3. hasStoredAnswers: answers exist in metadata (reload with saved answers)
  const hasStoredAnswers = answers && Array.isArray(answers) && answers.length > 0;
  const displayAnswers = isSubmitted ? collectedAnswers : (hasStoredAnswers ? answers : null);
  const showAnsweredView = (isAnswered || isSubmitted || hasStoredAnswers) && questions && displayAnswers;

  if (showAnsweredView) {
    return (
      <div className="question_message question_message_answered">
        <div className="question_answered_header">
          <CheckIcon size={16} />
          <Message
            id="builder_question_answered"
            defaultMessage="Questions answered"
          />
        </div>
        {questions.map((q, i) => (
          <div key={i} className="question_item">
            <span className="question_header_chip">{q.header}</span>
            <p className="question_text">{q.question}</p>
            <div className="options_list">
              {q.options?.map((opt, j) => {
                const isSelected = displayAnswers[i]?.selectedLabel?.includes(
                  opt.label
                );
                return (
                  <div
                    key={j}
                    className={`option_item ${isSelected ? "option_selected" : ""}`}
                  >
                    <span className="option_label">{opt.label}</span>
                    {opt.description && (
                      <span className="option_description">
                        {opt.description}
                      </span>
                    )}
                  </div>
                );
              })}
              {displayAnswers[i]?.customText && (
                <div className="other_answer">
                  <span className="other_label">
                    <Message id="builder_other" defaultMessage="Other" />:
                  </span>
                  <span className="other_text">{displayAnswers[i].customText}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Render pending wizard state
  if (isPending && currentQuestion) {
    return (
      <div className="question_message question_message_pending">
        {/* Progress dots */}
        {questions.length > 1 && (
          <div className="progress_dots">
            {questions.map((_, i) => (
              <span
                key={i}
                className={`progress_dot ${i === wizardIndex ? "dot_active" : ""} ${i < wizardIndex ? "dot_done" : ""}`}
              />
            ))}
          </div>
        )}

        {/* Current question */}
        <span className="question_header_chip">{currentQuestion.header}</span>
        <p className="question_text">{currentQuestion.question}</p>

        {/* Options */}
        <div className="options_list">
          {currentQuestion.options?.map((opt, i) => {
            const isSelected = selectedOptions.includes(opt.label);
            return (
              <div
                key={i}
                className={`option_item option_clickable ${currentQuestion.multiSelect ? "option_checkbox" : "option_radio"} ${isSelected ? "option_selected" : ""}`}
                onClick={() => handleSelectOption(opt.label)}
              >
                <div className="option_indicator">
                  {isSelected && <CheckIcon size={12} />}
                </div>
                <div className="option_content">
                  <span className="option_label">{opt.label}</span>
                  {opt.description && (
                    <span className="option_description">{opt.description}</span>
                  )}
                </div>
              </div>
            );
          })}

          {/* Other option */}
          <div
            className={`option_item option_clickable option_other ${currentQuestion.multiSelect ? "option_checkbox" : "option_radio"} ${isOtherSelected ? "option_selected" : ""}`}
            onClick={() => handleSelectOption("Other")}
          >
            <div className="option_indicator">
              {isOtherSelected && <CheckIcon size={12} />}
            </div>
            <div className="option_content">
              <span className="option_label">
                <Message id="builder_other" defaultMessage="Other" />
              </span>
            </div>
          </div>
        </div>

        {/* Other text input */}
        {isOtherSelected && (
          <input
            type="text"
            className="other_input"
            placeholder={label(intl, "builder_other_placeholder", "Please specify...")}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            autoFocus
          />
        )}

        {/* Next/Submit button */}
        <button
          className="question_next_btn"
          disabled={
            selectedOptions.length === 0 ||
            isSubmitting ||
            (isOtherSelected && !otherText.trim())
          }
          onClick={handleNext}
        >
          {isSubmitting ? (
            <>
              <Spinner size={18} />
              <Message id="builder_submitting" defaultMessage="Submitting..." />
            </>
          ) : isLastQuestion ? (
            <Message id="builder_submit" defaultMessage="Submit" />
          ) : (
            <Message id="builder_next" defaultMessage="Next" />
          )}
        </button>
      </div>
    );
  }

  // No valid state - render nothing
  return null;
};

export default QuestionMessage;
